import React, { useMemo } from "react";

import { useParams } from "react-router-dom";

import { useGetAllAdvisorTransitions } from "@fartherfinance/frontend/api/TransitionWorkflows/hooks/useGetAllAdvisorTransitions";
import { useGetTransitionDetailsDto } from "@fartherfinance/frontend/api/TransitionWorkflows/hooks/useGetTransitionDetailsDto";

import AdvisorContainer from "../../AdvisorContainer";
import AdvisorContentContainer from "../../AdvisorContentContainer";
import HorizontalNavigation, {
  HorizontalNavigationTab,
} from "@src/multiCustodian/components/HorizontalNavigation/HorizontalNavigation";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import { toClassName } from "@src/multiCustodian/utils/to-class-name";

import OnboardingTasks from "./components/OnboardingTasks";
import TransitionBreadcrumbs from "./components/TransitionBreadcrumbs/TransitionBreadcrumbs";
import TransitionGuides from "./components/TransitionGuides";
import TransitionNextTasksAndDetails from "./components/TransitionNextTasksAndDetails";
import TransitionOverview from "./components/TransitionOverview/TransitionOverview";
import TransitionProgress from "./components/TransitionProgress";
import TransitionTasks from "./components/TransitionTasks";
import TransitionTools from "./components/TransitionTools";
import { useRetry } from "./hooks/useRetry";
import TransitionSection from "./ui/TransitionSection/TransitionSection";

import styles from "./TransitionSummary.module.css";

interface TransitionSummaryProps {
  showBreadcrumbs?: boolean;
}

const TransitionSummary: React.FC<TransitionSummaryProps> = ({
  showBreadcrumbs = false,
}) => {
  const { transitionId } = useParams<Record<"transitionId", string>>();
  const auth = useAdvisorRequestAuth();
  const { handleRetry } = useRetry("transitions");
  const transitionDetailsQuery = useGetTransitionDetailsDto(transitionId, auth);
  const { data: advisorTransitions = [] } = useGetAllAdvisorTransitions(auth);

  const tabs: HorizontalNavigationTab[] = useMemo(
    () =>
      advisorTransitions.map((transition, index) => ({
        label: transition?.task?.custodian || `Custodian ${index + 1}`,
        path: `/Advisor/Transitions/${transition.transitionId}/Summary`,
      })),
    [advisorTransitions]
  );

  return (
    <AdvisorContainer>
      <AdvisorContentContainer heroTitle="Transitions" hasPadding={false}>
        {showBreadcrumbs && transitionDetailsQuery.data && (
          <TransitionBreadcrumbs transition={transitionDetailsQuery.data} />
        )}
        <HorizontalNavigation
          className={toClassName(
            styles.navigation,
            showBreadcrumbs && styles.resetMargin
          )}
          tabs={tabs.length > 1 ? tabs : []}
        >
          <TransitionSection>
            <TransitionProgress />
          </TransitionSection>

          <TransitionSection>
            <TransitionOverview
              transition={transitionDetailsQuery.data}
              isLoading={transitionDetailsQuery.isLoading}
              hasError={transitionDetailsQuery.hasError}
              onRetry={handleRetry}
            />
            <TransitionNextTasksAndDetails />
          </TransitionSection>

          <TransitionSection>
            <OnboardingTasks />
            <TransitionTasks />
          </TransitionSection>

          <TransitionSection>
            <TransitionGuides />
            <TransitionTools />
          </TransitionSection>
        </HorizontalNavigation>
      </AdvisorContentContainer>
    </AdvisorContainer>
  );
};

export default TransitionSummary;
